import type { ReferralStatus } from './enums';
import {
  isBlankChild,
  type EditReferralPayloadInput,
  type ReferralChildInput,
  type ReferralEditKind,
} from './dto/my-referrals';

export const REFERRAL_EDIT_LABELS: Record<ReferralEditKind, string> = {
  applicant_added: 'Applicant added',
  applicant_info_updated: 'Applicant information updated',
  screening_responses_updated: 'Screening responses updated',
  family_responses_updated: 'Family responses updated',
};

export const RESUBMITTABLE_STATUSES: readonly ReferralStatus[] = ['under_review', 'reviewed'];

export const RESUBMIT_HISTORY_REASON = 'Trustee edited the referral and resubmitted it to Admissions.';

export const isEditableAfterSubmit = (status: ReferralStatus) => status !== 'draft' && status !== 'archived';

export const resubmitMovesStatus = (status: ReferralStatus) => RESUBMITTABLE_STATUSES.includes(status);

const INFO_FIELDS = [
  'legalFirstName',
  'preferredName',
  'middleName',
  'lastName',
  'grade',
  'currentSchool',
  'city',
  'stateOrCountry',
] as const;

const ANSWER_FIELDS = [
  'relationship',
  'connectionNarrative',
  'communityContribution',
  'firstChoice',
  'enrollmentInterest',
  'additionalInformation',
] as const;

type Child = Omit<ReferralChildInput, 'childKey'> & { childKey?: string };

export interface ReferralEditState {
  payload: Pick<EditReferralPayloadInput, (typeof ANSWER_FIELDS)[number]> & { children: Child[] };
  countsTowardAllotment: boolean;
}

export interface ReferralEditDiff {
  kinds: ReferralEditKind[];
  addedChildren: number;
  infoChildren: number;
  screeningChildren: number;
  answersChanged: boolean;
  flagChanged: boolean;
  payloadChanged: boolean;
}

const trimmed = (v: string) => v.trim();

const screening = (c: Child) => [
  c.academicFamiliarity,
  c.academicFamiliarity === 'yes' ? trimmed(c.academicContext) : '',
  c.disciplinaryFamiliarity,
  c.disciplinaryFamiliarity === 'yes' ? trimmed(c.disciplinaryContext) : '',
];

const sameList = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((v, i) => v === b[i]);

export function referralEditChanges(before: ReferralEditState, after: ReferralEditState): ReferralEditDiff {
  const stored = new Map(before.payload.children.filter((c) => c.childKey).map((c) => [c.childKey!, c]));
  let addedChildren = 0;
  let infoChildren = 0;
  let screeningChildren = 0;
  for (const c of after.payload.children) {
    const old = c.childKey ? stored.get(c.childKey) : undefined;
    if (!old) {
      if (!isBlankChild(c)) addedChildren++;
      continue;
    }
    if (INFO_FIELDS.some((f) => trimmed(c[f]) !== trimmed(old[f]))) infoChildren++;
    if (!sameList(screening(c), screening(old))) screeningChildren++;
  }
  const answersChanged = ANSWER_FIELDS.some((f) => trimmed(after.payload[f]) !== trimmed(before.payload[f]));
  const flagChanged = after.countsTowardAllotment !== before.countsTowardAllotment;
  const kinds: ReferralEditKind[] = [];
  if (addedChildren) kinds.push('applicant_added');
  if (infoChildren) kinds.push('applicant_info_updated');
  if (screeningChildren) kinds.push('screening_responses_updated');
  if (answersChanged || flagChanged) kinds.push('family_responses_updated');
  return {
    kinds,
    addedChildren,
    infoChildren,
    screeningChildren,
    answersChanged,
    flagChanged,
    payloadChanged: addedChildren + infoChildren + screeningChildren > 0 || answersChanged,
  };
}
